/* global gtag, fbq */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, MapPin, Clock, Star, Users, Smartphone, Heart, Menu, X, Gift } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { database } from '../firebaseConfig';
import { ref, push, set } from 'firebase/database';

export default function BuySanJuan() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [popupEmail, setPopupEmail] = useState('');
  const [popupSubmitting, setPopupSubmitting] = useState(false);
  const [popupSuccess, setPopupSuccess] = useState(false);
  const [popupError, setPopupError] = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [riddleAnswer, setRiddleAnswer] = useState('');
  const [riddleSolved, setRiddleSolved] = useState(false);
  const [riddleHints, setRiddleHints] = useState(0);
  const [riddleShake, setRiddleShake] = useState(false);
  const [demoStep, setDemoStep] = useState(0); // 0=riddle, 1=solved, 2=directions, 3=recommendations
  const [showHistoric, setShowHistoric] = useState(false);

  // Exit-intent popup - scroll-up on mobile after 45 seconds
  useEffect(() => {
    const hasInteracted = localStorage.getItem('exitPopupDismissed_sanJuan');
    if (hasInteracted) return;

    let timeReached = false;
    let lastScrollY = window.scrollY;

    const timer = setTimeout(() => {
      timeReached = true;
    }, 45000);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (timeReached && currentScrollY < lastScrollY && currentScrollY > 100) {
        setShowExitPopup(true);
        if (typeof gtag !== 'undefined') {
          gtag('event', 'exit_popup_shown', { page: 'san-juan' });
        }
        window.removeEventListener('scroll', handleScroll);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handlePopupClose = () => {
    setShowExitPopup(false);
    localStorage.setItem('exitPopupDismissed_sanJuan', 'true');
    // Track popup dismissed in GA
    if (typeof gtag !== 'undefined') {
      gtag('event', 'exit_popup_dismissed', {
        page: 'san-juan'
      });
    }
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    if (!popupEmail || popupSubmitting) return;

    setPopupSubmitting(true);
    setPopupError('');

    try {
      // Save to Firebase Realtime Database
      const emailCapturesRef = ref(database, 'emailCaptures');
      const newEmailRef = push(emailCapturesRef);
      await set(newEmailRef, {
        email: popupEmail,
        source: 'exit_popup',
        page: 'san-juan',
        tourId: 'san-juan',
        promoCode: 'EXPLORE2026',
        createdAt: new Date().toISOString()
      });

      setPopupSuccess(true);
      localStorage.setItem('exitPopupDismissed_sanJuan', 'submitted');

      // Track successful submission in GA
      if (typeof gtag !== 'undefined') {
        gtag('event', 'exit_popup_submitted', {
          page: 'san-juan'
        });
      }
      // Track in Facebook Pixel
      if (typeof fbq !== 'undefined') {
        fbq('track', 'Lead', {
          content_name: 'Exit Popup Email Capture',
          content_category: 'san-juan'
        });
      }
    } catch (error) {
      console.error('Error submitting email:', error);
      setPopupError('Something went wrong. Please try again.');
    } finally {
      setPopupSubmitting(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('EXPLORE2026');
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'EXPLORE2026';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  // Show sticky CTA on scroll, hide near bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const nearBottom = scrollY + window.innerHeight > document.documentElement.scrollHeight - 300;
      setShowStickyCTA(scrollY > 300 && !nearBottom);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBuyNow = (location) => {
    // Track the click in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: 26.99,
        items: [{
          item_id: 'san-juan-tour',
          item_name: 'San Juan Scavenger Hunt Tour',
          price: 26.99,
          quantity: 1
        }],
        cta_location: location // hero, bottom, sticky, popup
      });
    }
    // Track in Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_ids: ['san-juan-tour'],
        content_type: 'product',
        value: 26.99,
        currency: 'USD'
      });
    }
    // Redirect to Stripe Payment Link
    // Use EXPLORE2026 (20% off) only for popup, otherwise use SAVE10 (10% off)
    const promoCode = location === 'popup' ? 'EXPLORE2026' : 'SAVE10';
    window.location.href = `https://buy.stripe.com/8x23coe3A3Z65tB0ylcZa01?prefilled_promo_code=${promoCode}`;
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>San Juan Puerto Rico Scavenger Hunt Walking Tour - Tour in a Box</title>
        <meta name="description" content="Explore Old San Juan with an interactive scavenger hunt tour. Solve riddles at 12 historic locations over 2 hours. Self-paced, works offline. Book now for $26.99!" />
        <meta name="keywords" content="San Juan scavenger hunt, Old San Juan walking tour, Puerto Rico treasure hunt, San Juan riddles, self-guided San Juan tour, things to do in Old San Juan" />
        <link rel="canonical" href="https://play.tourinabox.com/buy/san-juan" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content="https://play.tourinabox.com/buy/san-juan" />
        <meta property="og:title" content="San Juan Scavenger Hunt - Interactive Walking Tour" />
        <meta property="og:description" content="Solve riddles at 12 historic Old San Juan locations. Self-paced treasure hunt tour. Works offline. Perfect for couples, families & solo adventurers." />
        <meta property="og:image" content="https://play.tourinabox.com/images/sanjuan.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Colorful streets of Old San Juan, Puerto Rico" />
        <meta property="og:site_name" content="Tour in a Box" />
        <meta property="og:locale" content="en_US" />
        <meta property="product:price:amount" content="26.99" />
        <meta property="product:price:currency" content="USD" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://play.tourinabox.com/buy/san-juan" />
        <meta name="twitter:title" content="San Juan Scavenger Hunt Walking Tour" />
        <meta name="twitter:description" content="Interactive walking tour of Old San Juan. Solve riddles at 12 historic locations. Self-paced, works offline. Book your adventure!" />
        <meta name="twitter:image" content="https://play.tourinabox.com/images/sanjuan.webp" />
        <meta name="twitter:image:alt" content="Colorful streets of Old San Juan, Puerto Rico" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": "San Juan Scavenger Hunt Walking Tour",
            "description": "Interactive walking tour of Old San Juan with 12 riddle-based stops through historic landmarks. Self-paced treasure hunt experience.",
            "brand": {
              "@type": "Brand",
              "name": "Tour in a Box"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://play.tourinabox.com/buy/san-juan",
              "priceCurrency": "USD",
              "price": "26.99",
              "priceValidUntil": "2026-12-31",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Tour in a Box LLC"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "150",
              "bestRating": "5",
              "worstRating": "1"
            },
            "image": "https://play.tourinabox.com/images/sanjuan.webp",
            "category": "Tours & Activities"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://play.tourinabox.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "San Juan Tour",
                "item": "https://play.tourinabox.com/buy/san-juan"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Header with Logo */}
      <header className="pt-8 pb-6 px-4 border-b border-gray-200" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto relative flex items-center justify-between">
          {/* Language Toggle - left */}
          <div>
            <Link
              to="/buy/san-juan-es"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors hover:bg-gray-100"
              style={{ color: '#413C04', border: '1px solid #E4B4A6' }}
            >
              <span>🇪🇸</span>
              <span>Español</span>
            </Link>
          </div>

          {/* Logo - absolutely centered */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <img
              src="/PrimaryLogo.png"
              alt="Tour in a Box"
              className="h-[40px] sm:h-[48px]"
              style={{ objectFit: 'contain', width: 'auto' }}
            />
          </div>

          {/* Menu Button - right */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <Menu size={18} style={{ color: '#413C04' }} />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                style={{ fontFamily: 'Urbanist, sans-serif' }}
              >
                <a
                  href="https://www.tourinabox.com/store"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Browse All Tours
                </a>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(255, 250, 223, 0.5))' }}>
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url(/images/noise-512.png)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-12 sm:pb-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Video - Shows first on mobile, second on desktop */}
            <div className="relative order-first md:order-last">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="/videos/san-juan-hero.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                  poster="/images/sanjuan.webp"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Copy - Shows second on mobile, first on desktop */}
            <div className="order-last md:order-first">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                San Juan, Puerto Rico
              </h1>

              <p className="text-2xl font-bold mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#E4B4A6' }}>
                Scavenger Hunt Tour
              </p>

              {/* Social Proof - moved up */}
              <div
                className="flex items-center gap-4 mb-6 cursor-pointer transition-opacity hover:opacity-80"
                onClick={() => {
                  document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <div className="flex -space-x-2">
                  <img src="/images/customer1.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer2.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="/images/customer3.png" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <div className="flex items-center gap-1" style={{ color: '#F8662B' }}>
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                      <Star size={14} fill="currentColor" />
                    </div>
                    <span className="font-semibold" style={{ color: '#413C04' }}>4.8 out of 5</span>
                  </div>
                  <p style={{ color: '#413C04', opacity: 0.7 }}>
                    Loved by <span className="font-semibold" style={{ color: '#413C04', opacity: 1 }}>locals and visitors</span> alike
                  </p>
                </div>
              </div>

              <p className="text-lg mb-6" style={{ color: '#413C04', opacity: 0.75 }}>
                Solve riddles and unlock secrets as you explore Old San Juan with this self-guided tour through historic forts and hidden monuments.
              </p>

              {/* Key Benefits */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>12 historic stops, each with a riddle</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>1.5-hour self-paced adventure</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={16} style={{ color: '#385B37' }} />
                  </div>
                  <span style={{ color: '#413C04' }}>Play instantly or anytime in next year</span>
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2" style={{ borderColor: '#E4B4A6' }}>
                <div className="flex items-baseline justify-center gap-3 mb-4">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$26.99</span>
                  <span className="text-xl text-gray-400 line-through">$30</span>
                  <span className="bg-red-100 text-red-700 text-sm font-bold px-2 py-1 rounded">
                    Save 10%
                  </span>
                </div>

                <button
                  onClick={() => handleBuyNow('hero')}
                  className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                  style={{
                    background: '#F8662B',
                    fontFamily: 'Rubik, sans-serif'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#E05520'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#F8662B'}
                >
                  Get My Tour
                </button>

                <p className="text-sm text-center mt-3 mb-2" style={{ color: '#413C04', opacity: 0.7 }}>
                  Buy now, play anytime within a year
                </p>

                <div className="flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    1 year access
                  </span>
                  <span className="flex items-center gap-1">
                    <Check size={16} style={{ color: '#385B37' }} />
                    30-day refund
                  </span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Try It — Interactive Demo */}
      <section className="py-16" style={{ background: 'linear-gradient(to bottom, white, #FFFADF)' }}>
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Try a Sample Stop
          </h2>
          <p className="text-center mb-8" style={{ color: '#413C04', opacity: 0.7 }}>
            See how the tour works with a sample riddle
          </p>

          {/* Phone-style frame */}
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4" style={{ borderColor: '#413C04', background: '#413C04' }}>
            {/* Phone top bar */}
            <div className="flex items-center justify-between px-4 py-2" style={{ background: '#413C04' }}>
              <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>Current Location: El Morro</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(248, 102, 43, 0.3)', color: '#F8662B' }}>Stop 7 of 12</span>
            </div>

            {/* Screen content */}
            <div className="bg-white px-5 py-6" style={{ minHeight: 320 }}>

              {/* Card 0: Riddle */}
              {demoStep === 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.12)' }}>
                      <Star size={16} style={{ color: '#F8662B' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>RIDDLE</span>
                  </div>

                  <p className="text-lg mb-6 leading-relaxed" style={{ color: '#413C04' }}>
                    It crowns the cliffs with quiet might,<br />
                    A turning eye that cuts the night.<br />
                    No voice, no flame, yet still it warns,<br />
                    What keeps ships from ocean's thorns?
                  </p>

                  <div className={`mb-4 ${riddleShake ? 'animate-shake' : ''}`}>
                    <input
                      type="text"
                      value={riddleAnswer}
                      onChange={(e) => setRiddleAnswer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const a = riddleAnswer.trim().toLowerCase();
                          if (a.includes('lighthouse') || a.includes('faro') || a.includes('light house')) {
                            setRiddleSolved(true);
                            setDemoStep(1);
                          } else {
                            setRiddleShake(true);
                            setTimeout(() => setRiddleShake(false), 500);
                          }
                        }
                      }}
                      placeholder="Type your answer..."
                      className="w-full px-4 py-3 rounded-xl text-base outline-none"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', fontFamily: 'Urbanist, sans-serif' }}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const a = riddleAnswer.trim().toLowerCase();
                        if (a.includes('lighthouse') || a.includes('faro') || a.includes('light house')) {
                          setRiddleSolved(true);
                          setDemoStep(1);
                        } else {
                          setRiddleShake(true);
                          setTimeout(() => setRiddleShake(false), 500);
                        }
                      }}
                      className="flex-1 text-white font-bold py-3 rounded-xl transition-all"
                      style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                    >
                      Submit
                    </button>
                    <button
                      onClick={() => setRiddleHints(h => Math.min(h + 1, 2))}
                      className="px-4 py-3 rounded-xl font-semibold transition-all"
                      style={{ border: '2px solid #E4B4A6', color: '#413C04', background: 'white' }}
                    >
                      {riddleHints === 0 ? 'Hint?' : riddleHints === 1 ? 'More?' : 'Last hint'}
                    </button>
                  </div>

                  {riddleHints >= 1 && (
                    <div className="mt-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 It sits atop El Morro, guiding sailors safely to port
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <div className="mt-2 p-3 rounded-lg text-sm" style={{ background: 'rgba(56, 91, 55, 0.08)', color: '#385B37' }}>
                      💡 Its beam can be seen for miles across the ocean at night
                    </div>
                  )}
                  {riddleHints >= 2 && (
                    <button
                      onClick={() => {
                        setRiddleSolved(true);
                        setDemoStep(1);
                      }}
                      className="mt-3 text-sm font-semibold underline"
                      style={{ color: '#413C04', opacity: 0.5 }}
                    >
                      Reveal answer
                    </button>
                  )}
                </div>
              )}

              {/* Card 1: Solved! */}
              {demoStep === 1 && (
                <div>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                      <Check size={16} style={{ color: '#385B37' }} />
                    </div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#385B37' }}>
                      You got it!
                    </h3>
                  </div>
                  <p className="font-semibold mb-3 text-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>El Faro del Morro</p>
                  <div className="flex gap-3 mb-3">
                    <div className="relative rounded-xl overflow-hidden flex-shrink-0" style={{ aspectRatio: '3/4', width: '40%' }}>
                      <img
                        src="/images/ElMorroLight.jpg"
                        alt="El Faro del Morro lighthouse today"
                        className="w-full h-full object-cover absolute inset-0 transition-opacity duration-500"
                        style={{ opacity: showHistoric ? 0 : 1 }}
                      />
                      <img
                        src="/images/ElMorro-historic.jpg"
                        alt="El Faro del Morro lighthouse circa 1800s"
                        className="w-full h-full object-cover absolute inset-0 transition-opacity duration-500"
                        style={{ opacity: showHistoric ? 1 : 0 }}
                      />
                      <div className="absolute bottom-2 right-2 flex rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
                        <button
                          onClick={() => setShowHistoric(false)}
                          className="px-2.5 py-1 text-xs font-bold transition-all"
                          style={{ background: !showHistoric ? 'white' : 'transparent', color: !showHistoric ? '#413C04' : 'white', borderRadius: '9999px' }}
                        >
                          Today
                        </button>
                        <button
                          onClick={() => setShowHistoric(true)}
                          className="px-2.5 py-1 text-xs font-bold transition-all"
                          style={{ background: showHistoric ? 'white' : 'transparent', color: showHistoric ? '#413C04' : 'white', borderRadius: '9999px' }}
                        >
                          1800s
                        </button>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold uppercase tracking-wide mb-1" style={{ color: '#F8662B' }}>Learn about this site</p>
                      <ul className="text-sm space-y-1" style={{ color: '#413C04', opacity: 0.7 }}>
                        <li>• Before the lighthouse, El Morro used lanterns to warn ships at night</li>
                        <li>• Spain built the original in 1846, the first lighthouse in all of Puerto Rico</li>
                        <li>• The U.S. Navy destroyed it in 1898 during the Spanish-American War</li>
                        <li>• Rebuilt in 1908, it still flashes every 15 seconds to guide ships into port</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    onClick={() => setDemoStep(2)}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                  >
                    See what's next →
                  </button>
                </div>
              )}

              {/* Card 2: Directions preview */}
              {demoStep === 2 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                      <MapPin size={16} style={{ color: '#8FB3D3' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#8FB3D3', fontFamily: 'Rubik, sans-serif' }}>DIRECTIONS TO STOP 8</span>
                  </div>

                  <p className="text-lg mb-4 leading-relaxed" style={{ color: '#413C04' }}>
                    With your back to El Morro, find the <strong>yellow buildings with a red dome</strong>. Follow the road along the ocean until you reach a <strong>statue with four bronze figures</strong>.
                  </p>

                  <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(143, 179, 211, 0.1)', border: '1px solid rgba(143, 179, 211, 0.3)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock size={14} style={{ color: '#8FB3D3' }} />
                      <span className="text-sm font-semibold" style={{ color: '#413C04' }}>5 min walk</span>
                    </div>
                    <p className="text-xs" style={{ color: '#413C04', opacity: 0.6 }}>
                      Navigate by landmarks, with GPS backup if needed
                    </p>
                  </div>

                  <button
                    onClick={() => setDemoStep(3)}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#F8662B', fontFamily: 'Rubik, sans-serif' }}
                  >
                    One more thing →
                  </button>
                </div>
              )}

              {/* Card 3: Local recommendations */}
              {demoStep === 3 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.12)' }}>
                      <Heart size={16} style={{ color: '#F8662B' }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>LOCAL PICKS NEAR YOU</span>
                  </div>

                  <p className="text-sm mb-4" style={{ color: '#413C04', opacity: 0.7 }}>
                    At each stop, you also unlock local recommendations:
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(255, 250, 223, 0.8)', border: '1px solid rgba(228, 180, 166, 0.3)' }}>
                      <span className="text-xl">☕</span>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#413C04' }}>Café Don Ruiz</p>
                        <p className="text-xs" style={{ color: '#413C04', opacity: 0.6 }}>Single-origin Puerto Rican espresso and toasted mallorcas inside Ballajá</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBuyNow('demo')}
                    className="w-full text-white font-bold py-3 rounded-xl transition-all"
                    style={{ background: '#385B37', fontFamily: 'Rubik, sans-serif' }}
                  >
                    Get the full 12-stop tour →
                  </button>
                  <button
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="w-full mt-2 text-sm font-semibold py-2 transition-colors"
                    style={{ color: '#413C04', opacity: 0.5 }}
                  >
                    Keep learning more ↓
                  </button>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 py-3" style={{ background: '#413C04' }}>
              {[0, 1, 2, 3].map(i => (
                <button
                  key={i}
                  onClick={() => { if (i === 0 || riddleSolved) setDemoStep(i); }}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: demoStep === i ? 24 : 8,
                    height: 8,
                    background: demoStep === i ? '#F8662B' : 'rgba(255,255,255,0.3)',
                    cursor: (i === 0 || riddleSolved) ? 'pointer' : 'default',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Caption below phone */}
          <p className="text-center text-sm mt-6" style={{ color: '#413C04', opacity: 0.6 }}>
            {demoStep === 0 && !riddleSolved && 'Go ahead — try solving it!'}
            {demoStep === 1 && '12 stops, each with a unique riddle'}
            {demoStep === 2 && 'Navigate by landmarks, not GPS'}
            {demoStep === 3 && 'Discover hidden gems the whole way'}
            {demoStep === 0 && riddleSolved && 'You already solved it! Tap the dots to explore.'}
          </p>
        </div>

        {/* Shake animation */}
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-8px); }
            75% { transform: translateX(8px); }
          }
          .animate-shake { animation: shake 0.3s ease-in-out; }
        `}</style>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Why Explorers Love This Tour
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <MapPin size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Landmark-Based Directions</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Navigate using landmarks. You'll look up and truly notice the city.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                <Clock size={24} style={{ color: '#385B37' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Go At Your Pace</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Pause anytime for photos, food, or shopping. Your progress is saved.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Smartphone size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Works Offline</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Download once and use without internet connection.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(248, 102, 43, 0.15)' }}>
                <Star size={24} style={{ color: '#F8662B' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Engaging Riddles</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Fun challenges at each stop with hints if you need them.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(228, 180, 166, 0.4)' }}>
                <Users size={24} style={{ color: '#E4B4A6' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Perfect for Everyone</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Great for couples, families, friends, or solo adventurers.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(143, 179, 211, 0.2)' }}>
                <Heart size={24} style={{ color: '#8FB3D3' }} />
              </div>
              <div>
                <h3 className="font-bold mb-1" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Local Recommendations</h3>
                <p className="text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
                  Discover cafes, restaurants, and hidden gems at each stop.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Price Comparison */}
      <section className="py-16" style={{ background: '#FFFADF' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            How We Compare
          </h2>
          <p className="text-center mb-10" style={{ color: '#413C04', opacity: 0.7 }}>
            See how we stack up against other San Juan experiences
          </p>

          <div className="rounded-2xl overflow-hidden shadow-lg border" style={{ borderColor: '#E4B4A6' }}>
            {/* Header row */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center text-sm font-bold" style={{ background: '#413C04', color: 'white', fontFamily: 'Rubik, sans-serif' }}>
              <div className="py-3 px-2"></div>
              <div className="py-3 px-2 relative" style={{ background: '#F8662B' }}>
                Tour in a Box
              </div>
              <div className="py-3 px-2">Walking Tour</div>
              <div className="py-3 px-2">Escape Room</div>
            </div>

            {/* Price row */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-left pl-4 flex flex-col justify-center" style={{ color: '#413C04' }}>
                <div className="text-sm font-semibold" style={{ fontFamily: 'Rubik, sans-serif' }}>Price</div>
                <div className="text-xs leading-tight" style={{ opacity: 0.5 }}>for group of 3</div>
              </div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}>
                <span className="text-lg font-bold" style={{ color: '#F8662B', fontFamily: 'Rubik, sans-serif' }}>$27</span>
              </div>
              <div className="px-2 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$105</span>
                <span className="text-xs leading-tight" style={{ color: '#413C04', opacity: 0.5 }}>$35/person</span>
              </div>
              <div className="px-2 flex flex-col items-center justify-center">
                <span className="text-lg font-bold" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>$90</span>
                <span className="text-xs leading-tight" style={{ color: '#413C04', opacity: 0.5 }}>$30/person</span>
              </div>
            </div>

            {/* Private */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Private</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
            </div>

            {/* Your own pace */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>At your own pace</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Play anytime */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white border-b min-h-[52px]" style={{ borderColor: 'rgba(65, 60, 4, 0.1)' }}>
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Play anytime</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#385B37' }}><Check size={14} style={{ color: 'white' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
              <div className="px-2 flex items-center justify-center"><span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#e5e5e5' }}><X size={12} style={{ color: '#999' }} /></span></div>
            </div>

            {/* Fun & educational */}
            <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] text-center items-stretch bg-white min-h-[52px]">
              <div className="px-2 text-sm font-semibold text-left pl-4 flex items-center" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>Fun & educational</div>
              <div className="px-2 flex items-center justify-center" style={{ background: 'rgba(248, 102, 43, 0.08)' }}><span className="text-xs font-semibold" style={{ color: '#385B37' }}>Both</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Educational</span></div>
              <div className="px-2 flex items-center justify-center"><span className="text-xs" style={{ color: '#999' }}>Fun</span></div>
            </div>
          </div>

          <p className="text-center text-sm mt-4" style={{ color: '#413C04', opacity: 0.6 }}>
            Groups of 3 or fewer only need one purchase
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16" style={{ background: 'linear-gradient(to bottom right, rgba(255, 250, 223, 0.6), rgba(228, 180, 166, 0.4))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            What Explorers Are Saying
          </h2>
          <p className="text-center mb-12" style={{ color: '#413C04', opacity: 0.7 }}>
            Real reviews from real explorers
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved the scavenger hunt for our own big city of San Juan. What a great way to get out and see the city from a new perspective."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #8FB3D3, #6A92B3)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>AK</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Amber K.</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>San Juan Tour</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "We loved it! We went as a family and it was excellent."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #385B37, #2A4428)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>DA</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>DelAguaPR</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>San Juan Tour · <a href="https://share.google/3OprWt8jPXPP6VqCk" target="_blank" rel="noopener noreferrer" className="hover:underline">Google Review</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center gap-1 mb-3" style={{ color: '#F8662B' }}>
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
              <p className="mb-4 italic" style={{ color: '#413C04', opacity: 0.8 }}>
                "Our group loved the scavenger-hunt feel of the San Juan tour. Great idea and product."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: 'linear-gradient(to bottom right, #F8662B, #E05520)', fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}>NH</div>
                <div>
                  <div className="font-semibold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>Nancy H.</div>
                  <div className="text-sm" style={{ color: '#413C04', opacity: 0.6 }}>San Juan Tour</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
            Frequently Asked Questions
          </h2>

          <style>{`
            details summary {
              list-style: none;
              position: relative;
              padding-right: 2rem;
            }
            details summary::-webkit-details-marker {
              display: none;
            }
            details summary::after {
              content: '+';
              position: absolute;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              font-size: 1.5rem;
              font-weight: bold;
              color: #413C04;
              transition: transform 0.2s;
            }
            details[open] summary::after {
              content: '−';
            }
          `}</style>

          <div className="space-y-4">
            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Where does the tour start and how long does it take?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour begins at Old San Juan Cruise Port. Once you purchase, you'll receive detailed instructions on the exact starting point. It typically takes about 1.5 hours, but you can go at your own pace! Take breaks for photos, food, or shopping. Your progress is automatically saved.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Do I need to download an app?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                No! The tour works directly in your web browser on any smartphone. You can also add it to your home screen for offline access.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                When does my access expire?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Your access is valid for 1 full year from purchase. Start the tour whenever you're ready - today, next week, or next month!
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Can multiple people use one purchase?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Each purchase unlocks access for one account. Groups of 3 or fewer usually share one device, but larger groups often prefer having access on multiple devices so everyone can play along.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Can I gift this to someone?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                Yes! We offer gift certificates that make a great gift for travelers. <a href="mailto:hello@tourinabox.com" className="underline font-semibold" style={{ color: '#F8662B' }}>Email us</a> to purchase one.
              </p>
            </details>

            <details className="bg-white rounded-lg shadow-sm border p-4" style={{ borderColor: '#E4B4A6' }}>
              <summary className="font-semibold cursor-pointer" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                What languages is this available in?
              </summary>
              <p className="mt-2" style={{ color: '#413C04', opacity: 0.7 }}>
                The tour is available in English and Spanish. <Link to="/buy/san-juan-es" className="underline font-semibold" style={{ color: '#F8662B' }}>Click here for the Spanish version</Link>.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#F8662B' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Rubik, sans-serif' }}>
            Ready for an Adventure?
          </h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Join adventurers who've discovered Old San Juan in a whole new way
          </p>

          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto shadow-2xl">
            <div className="flex items-baseline justify-center gap-3 mb-2">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>$26.99</span>
              <span className="text-2xl text-gray-400 line-through">$30</span>
            </div>
            <p className="text-xs text-center mb-4" style={{ color: '#413C04', opacity: 0.6 }}>
              Discount applied automatically at checkout
            </p>
            <button
              onClick={() => handleBuyNow('bottom')}
              className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 mb-4"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2A2702'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#413C04'}
            >
              Get My Tour
            </button>
            <p className="text-sm text-center mt-2 mb-3" style={{ color: '#413C04', opacity: 0.7 }}>
              Buy now, play anytime within a year
            </p>
            <div className="flex items-center justify-center gap-4 text-sm" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>✓ 1 year access</span>
              <span>✓ 30-day refund</span>
            </div>
          </div>

          <p className="mt-6 text-sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Secure checkout powered by Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#413C04' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm" style={{ color: 'rgba(255, 250, 223, 0.7)' }}>
              © {new Date().getFullYear()} Tour in a Box LLC
            </div>
            <div className="flex gap-6 text-sm">
              <a href="mailto:hello@tourinabox.com" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Contact
              </a>
<a href="https://www.tourinabox.com/policies" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                Privacy
              </a>
              <a href="https://www.instagram.com/unboxyourtour" target="_blank" rel="noopener noreferrer" className="transition" style={{ color: 'rgba(255, 250, 223, 0.7)' }} onMouseEnter={(e) => e.currentTarget.style.color = '#FFFADF'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 250, 223, 0.7)'}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile-Only Sticky Bottom CTA */}
      {showStickyCTA && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50" style={{ borderColor: '#E4B4A6' }}>
          <div className="px-4 py-3">
            <button
              onClick={() => handleBuyNow('sticky')}
              className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all"
              style={{
                background: '#413C04',
                fontFamily: 'Rubik, sans-serif'
              }}
            >
              Get My Tour — $26.99
            </button>
            <div className="flex items-center justify-center gap-3 mt-2 text-xs" style={{ color: '#413C04', opacity: 0.7 }}>
              <span>Secure checkout with Stripe</span>
              <span>•</span>
              <span>30-day refund</span>
            </div>
          </div>
        </div>
      )}

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.6)' }}>
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            style={{ animation: 'popupSlideIn 0.3s ease-out' }}
          >
            {/* Close button */}
            <button
              onClick={handlePopupClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
              aria-label="Close"
            >
              <X size={20} style={{ color: '#413C04' }} />
            </button>

            {/* Header with gradient */}
            <div className="pt-8 pb-6 px-6 text-center" style={{ background: 'linear-gradient(to bottom right, #FFFADF, rgba(228, 180, 166, 0.3))' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#F8662B' }}>
                <Gift size={32} style={{ color: 'white' }} />
              </div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-3" style={{ background: '#385B37', color: 'white' }}>
                LIMITED TIME OFFER
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                Take <span style={{ color: '#F8662B' }}>20% off!</span>
              </h3>
              <p style={{ color: '#413C04', opacity: 0.8 }}>
                Double the savings! Enter your email for an exclusive code.
              </p>
            </div>

            {/* Form content */}
            <div className="p-6">
              {!popupSuccess ? (
                <form onSubmit={handlePopupSubmit}>
                  <div className="mb-4">
                    <input
                      type="email"
                      value={popupEmail}
                      onChange={(e) => setPopupEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-orange-400 transition-colors"
                      style={{ borderColor: '#E4B4A6', fontFamily: 'Urbanist, sans-serif' }}
                    />
                  </div>

                  {popupError && (
                    <p className="text-red-500 text-sm mb-4 text-center">{popupError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={popupSubmitting}
                    className="w-full text-white font-bold py-4 px-8 rounded-xl text-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: '#F8662B',
                      fontFamily: 'Rubik, sans-serif'
                    }}
                  >
                    {popupSubmitting ? 'Loading...' : 'Get My Discount'}
                  </button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(56, 91, 55, 0.15)' }}>
                    <Check size={32} style={{ color: '#385B37' }} />
                  </div>
                  <h4 className="text-xl font-bold mb-2" style={{ fontFamily: 'Rubik, sans-serif', color: '#413C04' }}>
                    Here's Your Code!
                  </h4>
                  <p className="mb-4" style={{ color: '#413C04', opacity: 0.8 }}>
                    Use this code at checkout for 20% off:
                  </p>

                  {/* Promo code box with copy button */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div
                      className="px-6 py-3 rounded-lg font-bold text-xl tracking-wider"
                      style={{
                        background: 'linear-gradient(to right, #FFFADF, rgba(228, 180, 166, 0.3))',
                        border: '2px dashed #F8662B',
                        fontFamily: 'Rubik, sans-serif',
                        color: '#413C04'
                      }}
                    >
                      EXPLORE2026
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="px-4 py-3 rounded-lg font-semibold transition-all"
                      style={{
                        background: codeCopied ? '#385B37' : '#F8662B',
                        color: 'white',
                        fontFamily: 'Urbanist, sans-serif'
                      }}
                    >
                      {codeCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>

                  <button
                    onClick={() => handleBuyNow('popup')}
                    className="w-full text-white font-bold py-3 px-6 rounded-xl text-base shadow-lg transition-all transform hover:scale-105 mb-3"
                    style={{
                      background: '#413C04',
                      fontFamily: 'Rubik, sans-serif'
                    }}
                  >
                    Use Code Now
                  </button>

                  <button
                    onClick={handlePopupClose}
                    className="text-sm font-semibold underline"
                    style={{ color: '#F8662B' }}
                  >
                    Continue browsing
                  </button>
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes popupSlideIn {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
        </div>
      )}

    </div>
    </>
  );
}
